/**
 * AxLabs Chemistry Experiments - Thermochemistry
 *
 * Collection of thermochemistry experiments generated using
 * the declarative framework.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState, getStateValues } from '../../physics/generated/helpers.js';
// ============================================================================
// HESS'S LAW VERIFICATION
// ============================================================================
export const HesssLawVerification = createExperimentFromSchema({
    id: 'hesss-law-verification',
    name: "Hess's Law Verification",
    domain: 'chemistry',
    description: 'Verification of Hess\'s Law using multi-step reactions',
    conceptTags: ['Hess\'s Law', 'Enthalpy', 'State Function', 'Thermodynamic Cycles'],
    difficulty: 5,
    stateVariables: [
        { id: 'step', name: 'Reaction Step', unit: '', initial: 0, symbol: 'N', description: 'Current step in the thermodynamic cycle' },
    ],
    parameters: [
        { id: 'DeltaH1', name: 'Step 1 Enthalpy', unit: 'kJ/mol', min: -500, max: 500, default: -393.5, symbol: '\\Delta H_1', description: 'Enthalpy change for step 1 (e.g., C + O2 → CO2: -393.5 kJ/mol)' },
        { id: 'DeltaH2', name: 'Step 2 Enthalpy', unit: 'kJ/mol', min: -500, max: 500, default: -110.5, symbol: '\\Delta H_2', description: 'Enthalpy change for step 2 (e.g., C + 0.5O2 → CO: -110.5 kJ/mol)' },
        { id: 'DeltaH3', name: 'Step 3 Enthalpy', unit: 'kJ/mol', min: -500, max: 500, default: -283, symbol: '\\Delta H_3', description: 'Enthalpy change for step 3 (e.g., CO + 0.5O2 → CO2: -283 kJ/mol)' },
        { id: 'T', name: 'Temperature', unit: 'K', min: 273, max: 500, default: 298.15, symbol: 'T', description: 'Reaction temperature' },
    ],
    equations: [
        {
            id: 'hesss-law',
            name: "Hess's Law",
            latex: '\\Delta H_{overall} = \\sum_i \\Delta H_i',
            description: 'Total enthalpy change is sum of individual steps',
        },
        {
            id: 'path-independence',
            name: 'Path Independence',
            latex: '\\Delta H_{direct} = \\Delta H_2 + \\Delta H_3',
            description: 'Direct path enthalpy equals multi-step path enthalpy',
        },
        {
            id: 'verification',
            name: 'Verification',
            latex: '\\text{Error} = \\frac{\\Delta H_1 - (\\Delta H_2 + \\Delta H_3)}{\\Delta H_1} \\times 100\\%',
            description: 'Percent difference verifying Hess\'s law',
        },
    ],
    derivatives: (_state, _params) => ({
        step: 0,
    }),
    derivedState: (_state, params) => {
        const DeltaH1 = getParam(params, 'DeltaH1');
        const DeltaH2 = getParam(params, 'DeltaH2');
        const DeltaH3 = getParam(params, 'DeltaH3');
        const DeltaH_direct = DeltaH2 + DeltaH3;
        const error = Math.abs(DeltaH1 - DeltaH_direct);
        const percent_error = (error / Math.abs(DeltaH1)) * 100;
        return {
            DeltaH_direct,
            DeltaH_step1: DeltaH1,
            DeltaH_step2: DeltaH2,
            DeltaH_step3: DeltaH3,
            DeltaH_total_path2: DeltaH2 + DeltaH3,
            hesss_verified: Math.abs(DeltaH1 - DeltaH_direct) < 0.1 ? 1 : 0,
            error,
            percent_error,
        };
    },
    failureModes: [
        {
            id: 'hesss-violated',
            name: "Hess's Law Violation",
            check: (_state, params) => {
                const DeltaH1 = getParam(params, 'DeltaH1');
                const DeltaH2 = getParam(params, 'DeltaH2');
                const DeltaH3 = getParam(params, 'DeltaH3');
                const DeltaH_direct = DeltaH2 + DeltaH3;
                return Math.abs(DeltaH1 - DeltaH_direct) > 1;
            },
            severity: 'warning',
            educationalMessage: 'The enthalpy values provided don\'t satisfy Hess\'s law. In a real thermodynamic cycle, ΔH(direct) must equal ΔH(indirect) because enthalpy is a state function.',
        },
    ],
    bounds: {
        step: { min: 0, max: 3 },
    },
});
// ============================================================================
// BOMB CALORIMETRY
// ============================================================================
export const BombCalorimetry = createExperimentFromSchema({
    id: 'bomb-calorimetry',
    name: 'Bomb Calorimetry',
    domain: 'chemistry',
    description: 'Measurement of heat of combustion using a bomb calorimeter',
    conceptTags: ['Calorimetry', 'Heat of Combustion', 'Heat Capacity', 'Adiabatic Process'],
    difficulty: 6,
    stateVariables: [
        { id: 'T', name: 'Temperature', unit: 'K', initial: 298.15, symbol: 'T', description: 'Temperature of the calorimeter water' },
    ],
    parameters: [
        { id: 'C_cal', name: 'Calorimeter Heat Capacity', unit: 'kJ/K', min: 5, max: 50, default: 10.5, symbol: 'C_{cal}', description: 'Heat capacity of calorimeter + water' },
        { id: 'm_sample', name: 'Sample Mass', unit: 'g', min: 0.1, max: 5, default: 1, symbol: 'm_{sample}' },
        { id: 'M_fuel', name: 'Fuel Molar Mass', unit: 'g/mol', min: 10, max: 300, default: 180.16, symbol: 'M_{fuel}', description: 'Glucose: 180.16 g/mol' },
        { id: 'T_initial', name: 'Initial Temperature', unit: 'K', min: 290, max: 300, default: 298.15, symbol: 'T_i' },
        { id: 'T_final', name: 'Final Temperature', unit: 'K', min: 298, max: 310, default: 301.5, symbol: 'T_f' },
    ],
    equations: [
        {
            id: 'heat-released',
            name: 'Heat Released',
            latex: 'q = C_{cal}(T_f - T_i)',
            description: 'Heat absorbed by calorimeter',
        },
        {
            id: 'heat-of-combustion',
            name: 'Heat of Combustion (per mole)',
            latex: '\\Delta H_c = -\\frac{q C_{cal}}{n} = -\\frac{C_{cal}(T_f - T_i)M_{fuel}}{m_{sample}}',
            description: 'Molar enthalpy of combustion',
        },
        {
            id: 'heat-of-combustion-gram',
            name: 'Heat of Combustion (per gram)',
            latex: '\\Delta H_c^{gram} = -\\frac{C_{cal}(T_f - T_i)}{m_{sample}}',
            description: 'Specific enthalpy of combustion',
        },
    ],
    derivatives: (_state, _params) => ({
        T: 0,
    }),
    derivedState: (_state, params) => {
        const C_cal = getParam(params, 'C_cal');
        const m_sample = getParam(params, 'm_sample');
        const M_fuel = getParam(params, 'M_fuel');
        const T_initial = getParam(params, 'T_initial');
        const T_final = getParam(params, 'T_final');
        const Delta_T = T_final - T_initial;
        const q = C_cal * Delta_T;
        const Delta_H_c_mol = -(q * M_fuel) / m_sample;
        const Delta_H_c_gram = -q / m_sample;
        const n_moles = m_sample / M_fuel;
        return {
            Delta_T,
            q,
            Delta_H_c_mol,
            Delta_H_c_gram,
            n_moles,
            Delta_H_c_kJ_mol: Delta_H_c_mol,
        };
    },
    failureModes: [
        {
            id: 'negative-mass',
            name: 'Negative Sample Mass',
            check: (_state, params) => {
                const m_sample = getParam(params, 'm_sample');
                return m_sample <= 0;
            },
            severity: 'error',
            educationalMessage: 'Sample mass must be positive.',
        },
        {
            id: 'temperature-decrease',
            name: 'Temperature Decreased',
            check: (_state, params) => {
                const T_initial = getParam(params, 'T_initial');
                const T_final = getParam(params, 'T_final');
                return T_final < T_initial;
            },
            severity: 'error',
            educationalMessage: 'In a combustion reaction, the temperature should increase. The final temperature must be greater than the initial temperature.',
        },
        {
            id: 'excessive-rise',
            name: 'Excessive Temperature Rise',
            check: (_state, params) => {
                const T_initial = getParam(params, 'T_initial');
                const T_final = getParam(params, 'T_final');
                return T_final - T_initial > 20;
            },
            severity: 'warning',
            educationalMessage: 'Temperature rise of more than 20 K is unusually high for a typical bomb calorimeter. Verify the sample mass and heat capacity values.',
        },
    ],
    bounds: {
        T: { min: 290, max: 320 },
    },
});
// ============================================================================
// ENTHALPY OF NEUTRALIZATION
// ============================================================================
export const EnthalpyOfNeutralization = createExperimentFromSchema({
    id: 'enthalpy-of-neutralization',
    name: 'Enthalpy of Neutralization',
    domain: 'chemistry',
    description: 'Heat released during acid-base neutralization',
    conceptTags: ['Neutralization', 'Enthalpy', 'Acid-Base', 'Calorimetry', 'Strong Electrolytes'],
    difficulty: 5,
    stateVariables: [
        { id: 'V_added', name: 'Base Volume Added', unit: 'mL', initial: 0, symbol: 'V_{added}', description: 'Volume of base added to acid' },
    ],
    parameters: [
        { id: 'C_acid', name: 'Acid Concentration', unit: 'M', min: 0.1, max: 5, default: 1, symbol: 'C_{acid}' },
        { id: 'V_acid', name: 'Acid Volume', unit: 'mL', min: 10, max: 100, default: 50, symbol: 'V_{acid}' },
        { id: 'C_base', name: 'Base Concentration', unit: 'M', min: 0.1, max: 5, default: 1, symbol: 'C_{base}' },
        { id: 'DeltaH_neutral', name: 'Standard Enthalpy of Neutralization', unit: 'kJ/mol', min: -80, max: -50, default: -55.8, symbol: '\\Delta H_{neut}', description: 'Strong acid-strong base: -55.8 kJ/mol' },
        { id: 'rho_solution', name: 'Solution Density', unit: 'g/mL', min: 0.9, max: 1.1, default: 1.02, symbol: '\\rho' },
        { id: 'c_p_solution', name: 'Specific Heat Capacity', unit: 'J/(g·K)', min: 3.5, max: 4.5, default: 4.18, symbol: 'c_p' },
        { id: 'T_initial', name: 'Initial Temperature', unit: 'K', min: 290, max: 300, default: 298.15, symbol: 'T_i' },
    ],
    equations: [
        {
            id: 'neutralization-reaction',
            name: 'Neutralization Reaction',
            latex: 'H^+ + OH^- \\rightarrow H_2O',
            description: 'Net ionic equation for strong acid-strong base neutralization',
        },
        {
            id: 'heat-released',
            name: 'Heat Released',
            latex: 'q = -\\Delta H_{neut} \\times n_{H_2O}',
            description: 'Heat released during neutralization',
        },
        {
            id: 'temperature-change',
            name: 'Temperature Change',
            latex: '\\Delta T = \\frac{-\\Delta H_{neut} \\cdot n_{H_2O}}{m_{solution} \\cdot c_p}',
            description: 'Temperature increase of solution',
        },
    ],
    derivatives: (_state, _params) => ({
        V_added: 0,
    }),
    derivedState: (state, params) => {
        const V_added = getState(state, 'V_added');
        const C_acid = getParam(params, 'C_acid');
        const V_acid = getParam(params, 'V_acid');
        const C_base = getParam(params, 'C_base');
        const DeltaH_neutral = getParam(params, 'DeltaH_neutral');
        const rho_solution = getParam(params, 'rho_solution');
        const c_p_solution = getParam(params, 'c_p_solution');
        const T_initial = getParam(params, 'T_initial');
        const V_eq = (C_acid * V_acid) / C_base;
        const V_total = V_acid + V_added;
        let moles_water = 0;
        let T_final = T_initial;
        if (V_added <= V_eq) {
            moles_water = C_base * V_added / 1000;
            const mass_solution = rho_solution * V_total;
            const q = -DeltaH_neutral * moles_water * 1000;
            const Delta_T = q / (mass_solution * c_p_solution);
            T_final = T_initial + Delta_T;
        }
        else {
            moles_water = C_acid * V_acid / 1000;
            const mass_solution = rho_solution * V_total;
            const q = -DeltaH_neutral * moles_water * 1000;
            const Delta_T = q / (mass_solution * c_p_solution);
            T_final = T_initial + Delta_T;
        }
        return {
            V_eq,
            moles_water,
            mass_solution: rho_solution * V_total,
            Delta_T: T_final - T_initial,
            T_final,
            at_equivalence: Math.abs(V_added - V_eq) < 0.5 ? 1 : 0,
            q_total: -DeltaH_neutral * moles_water,
        };
    },
    failureModes: [
        {
            id: 'negative-volume',
            name: 'Negative Volume Added',
            check: (_state, _params) => {
                const V_added = getState(getStateValues(_state), 'V_added');
                return V_added < 0;
            },
            severity: 'error',
            educationalMessage: 'Added volume cannot be negative.',
        },
    ],
    bounds: {
        V_added: { min: 0, max: 200 },
    },
});
// ============================================================================
// ENTHALPY OF SOLUTION
// ============================================================================
export const EnthalpyOfSolution = createExperimentFromSchema({
    id: 'enthalpy-of-solution',
    name: 'Enthalpy of Solution',
    domain: 'chemistry',
    description: 'Heat absorbed or released when dissolving a solute',
    conceptTags: ['Enthalpy of Solution', 'Solvation', 'Lattice Energy', 'Hydration Energy'],
    difficulty: 5,
    stateVariables: [
        { id: 'm_dissolved', name: 'Mass Dissolved', unit: 'g', initial: 0, symbol: 'm_{dissolved}', description: 'Mass of solute that has dissolved' },
    ],
    parameters: [
        { id: 'DeltaH_sol', name: 'Enthalpy of Solution', unit: 'kJ/mol', min: -100, max: 100, default: 3.9, symbol: '\\Delta H_{sol}', description: 'Ammonium nitrate: +3.9 kJ/mol (endothermic), NaOH: -44.5 kJ/mol (exothermic)' },
        { id: 'M_solute', name: 'Solute Molar Mass', unit: 'g/mol', min: 20, max: 300, default: 80.04, symbol: 'M_{solute}', description: 'Ammonium nitrate: 80.04 g/mol' },
        { id: 'm_solvent', name: 'Solvent Mass', unit: 'g', min: 50, max: 500, default: 100, symbol: 'm_{solvent}' },
        { id: 'c_p_solvent', name: 'Solvent Specific Heat', unit: 'J/(g·K)', min: 3.5, max: 4.5, default: 4.18, symbol: 'c_p' },
        { id: 'T_initial', name: 'Initial Temperature', unit: 'K', min: 290, max: 300, default: 298.15, symbol: 'T_i' },
    ],
    equations: [
        {
            id: 'solution-process',
            name: 'Solution Process',
            latex: '\\Delta H_{sol} = \\Delta H_{lattice} + \\Delta H_{hydration}',
            description: 'Enthalpy of solution equals lattice energy plus hydration energy',
        },
        {
            id: 'heat-exchange',
            name: 'Heat Exchange',
            latex: 'q = -m_{solvent} c_p \\Delta T',
            description: 'Heat absorbed by the solution',
        },
        {
            id: 'enthalpy-per-mole',
            name: 'Enthalpy per Mole',
            latex: '\\Delta H_{sol} = \\frac{q}{n_{solute}}',
            description: 'Molar enthalpy of solution',
        },
    ],
    derivatives: (_state, _params) => ({
        m_dissolved: 0,
    }),
    derivedState: (state, params) => {
        const m_dissolved = getState(state, 'm_dissolved');
        const DeltaH_sol = getParam(params, 'DeltaH_sol');
        const M_solute = getParam(params, 'M_solute');
        const m_solvent = getParam(params, 'm_solvent');
        const c_p_solvent = getParam(params, 'c_p_solvent');
        const T_initial = getParam(params, 'T_initial');
        const n_solute = m_dissolved / M_solute;
        const q_expected = DeltaH_sol * n_solute * 1000;
        const Delta_T = -q_expected / (m_solvent * c_p_solvent);
        const T_final = T_initial + Delta_T;
        const endothermic = DeltaH_sol > 0 ? 1 : 0;
        return {
            n_solute,
            q_expected,
            Delta_T,
            T_final,
            endothermic,
            Delta_T_magnitude: Math.abs(Delta_T),
            molality: n_solute / (m_solvent / 1000),
        };
    },
    failureModes: [
        {
            id: 'negative-mass',
            name: 'Negative Mass Dissolved',
            check: (_state, _params) => {
                const m_dissolved = getState(getStateValues(_state), 'm_dissolved');
                return m_dissolved < 0;
            },
            severity: 'error',
            educationalMessage: 'Dissolved mass cannot be negative.',
        },
        {
            id: 'excess-solute',
            name: 'Exceeding Solubility Limit',
            check: (_state, params) => {
                const m_dissolved = getState(getStateValues(_state), 'm_dissolved');
                const m_solvent = getParam(params, 'm_solvent');
                return m_dissolved > m_solvent;
            },
            severity: 'warning',
            educationalMessage: 'The amount of solute exceeds typical solubility limits. Some solid may remain undissolved.',
        },
    ],
    bounds: {
        m_dissolved: { min: 0, max: 200 },
    },
});
//# sourceMappingURL=thermochemistry.js.map